import {
  ArdoqId,
  FilterInterfaceFilter,
  FormattingFilter,
  GroupingRule,
  isFormattingFilter,
  ViewIds,
} from '@ardoq/api-types';
import { resetState } from '@ardoq/common-helpers';
import {
  DependencyMapViewSettings,
  DependencyMapViewStreamedProps,
  readDependencyMapViewSettings,
  setFocusedItem,
  setHoveredItem,
  type FocusedHoveredState as InteractiveStates,
} from '@ardoq/dependency-map';
import { isEqual } from 'lodash';
import type { HasViewInstanceId } from '@ardoq/graph';
import {
  action$,
  collectRoutines,
  combineReducers,
  dispatchAction,
  ofType,
  reducer,
  streamReducer,
  withNamespace,
} from '@ardoq/rxbeach';
import { mainAppModuleSidebar$ } from 'appContainer/MainAppModule/MainAppModuleSidebar/mainAppModuleSidebar$';
import { updateViewEmptyStateData } from 'appLayout/ardoqStudio/emptyState/actions';
import { isUpdatingAppState$ } from 'isUpdatingAppState$';
import {
  EMPTY_LOADED_SCENARIO_DATA,
  loadedScenarioData$,
} from 'loadedScenarioData$';
import {
  EnhancedScopeData,
  FormattingAndLabelRules,
  getEmptyGraphModel,
  GlobalReferenceTypeId,
  type LoadedGraph,
} from '@ardoq/data-model';
import { scopeDataOperations, type FilterColorData } from '@ardoq/scope-data';
import { graphModel$ } from 'modelInterface/graphModel$';
import { groupingRuleInterface } from 'modelInterface/groupingRules/groupingRuleInterface';
import { combineLatest, Observable, of, pipe } from 'rxjs';
import { isPresentationMode } from 'env';
import {
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';
import { scenarioRelatedComponents$ } from 'scenarioRelated/scenarioRelatedComponents$';
import {
  emptyScenarioRelatedComponentIds,
  scenarioRelatedComponentIds$,
} from 'streams/components/components$';
import {
  groupingRules$,
  type GroupingRulesState,
} from 'streams/grouping/groupingRules$';
import { notifyFiltersChanged } from 'streams/filters/FilterActions';
import { context$ } from 'streams/context/context$';
import { EMPTY_CONTEXT_SHAPE } from 'streams/context/ContextShape';
import type { ViewStatusReporter } from 'tabview/activeViewsStatus/types';
import { reportDataWorkStarted } from 'tabview/activeViewsStatus/viewStatusReporter';
import {
  DEFAULT_EXPECTED_DURATION,
  recordViewModelReady,
  startViewLoadTracking,
} from 'tracking/durationTracking/viewPerformance';
import { isViewpointMode$, loadedGraph$ } from 'traversals/loadedGraph$';
import {
  ViewpointModeInstanceViewSettings,
  WorkspaceModeInstanceViewSettings,
} from 'views/environmentals/viewSettings/types';
import { viewIdToUserFriendlyName } from 'views/metaInfoConsts';
import { loadedGraphOperations } from '../../traversals/loadedGraphOperations';
import { bypassLimit } from './actions';
import { componentOperations } from '@ardoq/core';
import { getScopeDataFilterChecks } from './scopeDataFilterChecks';
import {
  buildWorkspaceModeViewModel,
  buildViewpointModeViewModel,
  type DependencyMapViewInterface,
} from './buildViewModel';
import { workspaceModeScopeData$ } from 'streams/scopeData/workspaceModeScopeData$';
import { ReferenceLabelSource } from '@ardoq/data-model';
import {
  buildReferenceFilterContext,
  filterInterface,
  type ReferenceFilterContext,
} from '@ardoq/filter-interface';
import {
  getAllReferenceLabelFilters,
  getGraphInterfaceForEnhancedScopeData,
  getGraphInterfaceWithModelInterfaces,
  getIsShowReferenceTypeApplied,
  getLabelsInterfaceForEnhancedScopeData,
  getLabelsInterfaceWithModelInterfaces,
  getReferenceLabelFilter,
} from '@ardoq/graph';
import { warmFilterCacheHack } from 'collections/warmFilterCacheHack';
import { getPerformanceTrackingData } from './perfomanceTrackingUtils';
import {
  BuildViewModelArgs,
  BuildViewpointModeArgs,
  BuildWorkspaceModeArgs,
  DependencyMapGroupingData,
  DependencyMapViewModelState,
} from './types';
import {
  getDependencyMapGroupingData,
  resolveLegendConditionalFormatting,
} from './util';
import { getActiveConditionalFormattingForLegend } from '@ardoq/view-legend';
import { getDependencyMapViewComponentIds } from './viewComponentIds';
import { locale$ } from 'streams/locale$';
import { getDefaultLocale } from '@ardoq/locale';
import { orgUsers$ } from 'streams/orgUsers/orgUsers$';
import { orgUsersById$ } from 'tabview/relationshipDiagrams/shared/sharedGlobalStreams';
import { usersOperations } from '@ardoq/core';
import { clearGraphSelection } from '@ardoq/component-selection';
import { referenceOperations } from '@ardoq/reference-operations';
import { formattingAndLabelRules$ } from 'streams/filters/formattingAndLabelRules$';
import { DEFAULT_ARDOQ_I18NLOCALE } from '@ardoq/i18n';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { readViewComponentIds } from 'streams/viewComponentIds$';

const VIEW_ID = ViewIds.DEPENDENCY_MAP_2;

const EMPTY_SCOPE_DATA: EnhancedScopeData = scopeDataOperations.enhance(
  scopeDataOperations.getEmpty(),
  { shouldFreeze: false }
);

const EMPTY_FORMATTING_AND_LABEL_RULES: FormattingAndLabelRules = { rules: [] };

export type DependencyMapSourceData = {
  viewModelArgs: BuildViewModelArgs;
  viewInstanceId: string;
  performanceTrackingId: string | null;
  bypassLimit: boolean;
};

const getEmptyState = (
  defaultViewSettings: DependencyMapViewSettings
): DependencyMapSourceData => ({
  viewModelArgs: {
    graphModel: getEmptyGraphModel(),
    context: EMPTY_CONTEXT_SHAPE,
    viewSettings: defaultViewSettings,
    loadedGraph: loadedGraphOperations.getEmpty(),
    loadedScenarioData: EMPTY_LOADED_SCENARIO_DATA,
    scenarioRelatedComponents: { componentIds: [] },
    scenarioRelatedComponentIds: emptyScenarioRelatedComponentIds,
    showScopeRelated: false,
    locale: getDefaultLocale(),
    users: [],
    i18nLocale: DEFAULT_ARDOQ_I18NLOCALE,
    orgUsersById: {},
    scopeData: EMPTY_SCOPE_DATA,
    groupingRules: [],
    isPresentationMode: false,
    _filterTick: null,
  },
  viewInstanceId: '',
  performanceTrackingId: null,
  bypassLimit: false,
});

export const emptyFocusedHoveredState: InteractiveStates = {
  hoveredComponentId: null,
  focusedComponentId: null,
};

export type GetDependencyMapViewModelArgs = HasViewInstanceId & {
  defaultViewSettings: DependencyMapViewSettings;
  viewSettingsWithChanges$: Observable<
    | ViewpointModeInstanceViewSettings<ViewIds.DEPENDENCY_MAP_2>
    | WorkspaceModeInstanceViewSettings<ViewIds.DEPENDENCY_MAP_2>
  >;
  viewStatusReporter: ViewStatusReporter;
};

const activeOrgUsers$ = orgUsers$.pipe(
  map(({ users }) => usersOperations.getAllOrgUsers(users))
);

const toGroupingRulesForView = (state: GroupingRulesState): GroupingRule[] =>
  state.groupingRules.map(groupingRuleInterface.toInterfaceType);

const commonViewModelArgs = (
  viewSettings$: Observable<DependencyMapViewSettings>
) => ({
  context: context$,
  viewSettings: viewSettings$,
  loadedGraph: loadedGraph$,
  loadedScenarioData: loadedScenarioData$,
  scenarioRelatedComponents: scenarioRelatedComponents$,
  scenarioRelatedComponentIds: scenarioRelatedComponentIds$,
  showScopeRelated: mainAppModuleSidebar$.pipe(
    map(({ showScopeRelated }) => showScopeRelated)
  ),
  locale: locale$,
  users: activeOrgUsers$,
  i18nLocale: i18nLocale$,
  orgUsersById: orgUsersById$,
  groupingRules: groupingRules$.pipe(map(toGroupingRulesForView)),
  isPresentationMode: of(isPresentationMode()),
});

const buildWorkspaceModeArgs$ = (
  viewSettings$: Observable<DependencyMapViewSettings>
): Observable<BuildWorkspaceModeArgs> =>
  combineLatest({
    ...commonViewModelArgs(viewSettings$),
    graphModel: graphModel$,
    scopeData: workspaceModeScopeData$,
    _filterTick: action$.pipe(ofType(notifyFiltersChanged), startWith(null)),
  }).pipe(shareReplay({ bufferSize: 1, refCount: true }));

const buildViewpointModeArgs$ = (
  viewSettings$: Observable<DependencyMapViewSettings>
): Observable<BuildViewpointModeArgs> =>
  combineLatest({
    ...commonViewModelArgs(viewSettings$),
    formattingAndLabelRules: formattingAndLabelRules$,
  }).pipe(
    map(args => ({
      ...args,
      scopeData: args.loadedGraph.scopeData,
      graphModel: args.loadedGraph.scopeData.graphData,
    })),
    shareReplay({ bufferSize: 1, refCount: true })
  );

export const shapeResetArgs = (
  viewInstanceId: string,
  viewModelArgs: BuildViewModelArgs,
  performanceTrackingId: string | null
): DependencyMapSourceData => ({
  viewModelArgs,
  viewInstanceId,
  performanceTrackingId,
  bypassLimit: false,
});

export const getViewModel$ = ({
  viewInstanceId,
  viewSettingsWithChanges$,
  defaultViewSettings,
  viewStatusReporter,
}: GetDependencyMapViewModelArgs) => {
  const viewSettings$ = viewSettingsWithChanges$.pipe(
    map(({ currentSettings }) => readDependencyMapViewSettings(currentSettings))
  );

  const viewModelArgs$: Observable<BuildViewModelArgs> = isViewpointMode$.pipe(
    switchMap(({ isViewpointMode }) =>
      isViewpointMode
        ? buildViewpointModeArgs$(viewSettings$)
        : buildWorkspaceModeArgs$(viewSettings$)
    )
  );

  const resetViewModel$ = combineLatest({
    viewModelArgs: viewModelArgs$,
    isUpdatingAppState: isUpdatingAppState$,
  }).pipe(
    reportDataWorkStarted(viewStatusReporter, 'reset'),
    filter(({ isUpdatingAppState }) => !isUpdatingAppState),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  const bypassLimit$ = action$.pipe(
    ofType(bypassLimit),
    reportDataWorkStarted(viewStatusReporter, 'bypassLimit')
  );

  const coreViewModel$ = action$.pipe(
    combineReducers(getEmptyState(defaultViewSettings), [
      streamReducer(resetViewModel$, (_previousState, { viewModelArgs }) =>
        shapeResetArgs(
          viewInstanceId,
          viewModelArgs,
          startViewLoadTracking(
            viewIdToUserFriendlyName[VIEW_ID],
            DEFAULT_EXPECTED_DURATION
          )
        )
      ),
      streamReducer(bypassLimit$, onBypassLimit),
    ]),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  const interactiveStates$ = action$.pipe(
    withNamespace(viewInstanceId),
    combineReducers(emptyFocusedHoveredState, [
      streamReducer(resetViewModel$, resetState(emptyFocusedHoveredState)),
      reducer(setHoveredItem, handleHoveredComponentChanged),
      reducer(setFocusedItem, handleFocusedComponentChanged),
      reducer(clearGraphSelection, handleClearGraphSelection),
    ]),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  return [coreViewModel$, interactiveStates$] as const;
};

export const onBypassLimit = (
  state: DependencyMapSourceData
): DependencyMapSourceData => ({
  ...state,
  bypassLimit: true,
});

export const handleHoveredComponentChanged = (
  state: InteractiveStates,
  hoveredComponentId: string | null
): InteractiveStates => ({
  ...state,
  hoveredComponentId,
});

export const handleFocusedComponentChanged = (
  state: InteractiveStates,
  focusedComponentId: string | null
): InteractiveStates => {
  const hasFocusedChanged = focusedComponentId !== state.focusedComponentId;
  return {
    ...state,
    hoveredComponentId: hasFocusedChanged ? state.hoveredComponentId : null,
    focusedComponentId: hasFocusedChanged ? focusedComponentId : null,
  };
};

export const handleClearGraphSelection = (
  state: InteractiveStates
): InteractiveStates => ({
  ...state,
  hoveredComponentId: null,
  focusedComponentId: null,
});

type SharedRenderSetup = {
  scopeData: EnhancedScopeData;
  viewInterface: DependencyMapViewInterface;
  referenceIds: ArdoqId[];
  referenceFilterContext: ReferenceFilterContext;
  filterColorData: FilterColorData;
  globalTypeIdByReferenceId: ReadonlyMap<ArdoqId, GlobalReferenceTypeId | null>;
  groupingRules: GroupingRule[];
  formattingFilters: (FormattingFilter | FilterInterfaceFilter)[];
  isShowReferenceTypeApplied: boolean;
};

type ViewpointRenderSetup = SharedRenderSetup & {
  groupingData: DependencyMapGroupingData;
};

type CommonRenderData = {
  referenceIds: ArdoqId[];
  referenceFilterContext: ReferenceFilterContext;
  filterColorData: FilterColorData;
  globalTypeIdByReferenceId: ReadonlyMap<ArdoqId, GlobalReferenceTypeId | null>;
};

const prepareCommonRenderData = ({
  viewModelArgs: { graphModel, scopeData, loadedGraph },
}: DependencyMapSourceData): CommonRenderData => {
  const referenceIds = Array.from(graphModel.referenceMap.keys());
  const referenceFilterContext = buildReferenceFilterContext(scopeData);
  const filterColorData = warmFilterCacheHack({
    scopeData,
    scopeSource: loadedGraph,
    graphModel,
    referenceFilterContext,
  });
  const globalTypeIdByReferenceId = new Map(
    referenceIds.map(referenceId => {
      const reference = scopeData.referencesById[referenceId];
      return [
        referenceId,
        reference !== undefined
          ? referenceOperations.getGlobalTypeId(reference)
          : null,
      ] as const;
    })
  );
  return {
    referenceIds,
    referenceFilterContext,
    filterColorData,
    globalTypeIdByReferenceId,
  };
};

const prepareWorkspaceModeRender = (
  viewModelState: DependencyMapSourceData
): SharedRenderSetup => {
  const { scopeData, groupingRules } = viewModelState.viewModelArgs;
  const viewInterface: DependencyMapViewInterface = {
    ...getGraphInterfaceWithModelInterfaces(),
    ...getLabelsInterfaceWithModelInterfaces(),
  };
  const {
    referenceIds,
    referenceFilterContext,
    filterColorData,
    globalTypeIdByReferenceId,
  } = prepareCommonRenderData(viewModelState);
  return {
    scopeData,
    viewInterface,
    referenceIds,
    referenceFilterContext,
    filterColorData,
    globalTypeIdByReferenceId,
    groupingRules,
    formattingFilters: filterInterface.getFormattingFilters(),
    isShowReferenceTypeApplied: getIsShowReferenceTypeApplied(
      filterInterface.getAllReferenceLabelFilters()
    ),
  };
};

const resolveFormattingAndLabelRules = (
  viewModelArgs: BuildViewModelArgs
): FormattingAndLabelRules =>
  'formattingAndLabelRules' in viewModelArgs
    ? viewModelArgs.formattingAndLabelRules
    : EMPTY_FORMATTING_AND_LABEL_RULES;

const prepareViewpointModeRender = (
  viewModelState: DependencyMapSourceData
): ViewpointRenderSetup => {
  const {
    scopeData,
    groupingRules,
    i18nLocale,
    orgUsersById,
    loadedScenarioData,
    showScopeRelated,
    scenarioRelatedComponents,
  } = viewModelState.viewModelArgs;
  const formattingFilters = resolveFormattingAndLabelRules(
    viewModelState.viewModelArgs
  ).rules;
  const groupingData = getDependencyMapGroupingData({
    loadedScenarioData,
    showScopeRelated,
    scenarioRelatedComponents,
    groupingRules,
  });
  const viewInterface: DependencyMapViewInterface = {
    ...getGraphInterfaceForEnhancedScopeData({
      enhancedScopeData: scopeData,
      conditionalFormatting: formattingFilters,
      groupingRules: groupingData.appliedGroupingRules,
      i18nLocale,
    }),
    ...getLabelsInterfaceForEnhancedScopeData({
      enhancedScopeData: scopeData,
      formattingFilters,
      i18nLocale,
      formatUserFieldFn: val =>
        usersOperations.formatUserField(orgUsersById, val),
    }),
  };
  const {
    referenceIds,
    referenceFilterContext,
    filterColorData,
    globalTypeIdByReferenceId,
  } = prepareCommonRenderData(viewModelState);
  return {
    scopeData,
    viewInterface,
    referenceIds,
    referenceFilterContext,
    filterColorData,
    globalTypeIdByReferenceId,
    groupingRules,
    groupingData,
    formattingFilters,
    isShowReferenceTypeApplied: getIsShowReferenceTypeApplied(
      getAllReferenceLabelFilters(formattingFilters)
    ),
  };
};

export const toWorkspaceModeViewModel = (
  viewModelState: DependencyMapSourceData
): DependencyMapViewModelState => {
  const {
    scopeData,
    viewInterface,
    referenceFilterContext,
    filterColorData,
    globalTypeIdByReferenceId,
    groupingRules,
    formattingFilters,
    isShowReferenceTypeApplied,
  } = prepareWorkspaceModeRender(viewModelState);
  const viewModel = buildWorkspaceModeViewModel(
    viewModelState.viewModelArgs,
    viewModelState.bypassLimit,
    scopeData,
    filterColorData,
    viewInterface,
    isShowReferenceTypeApplied,
    formattingFilters,
    groupingRules,
    globalTypeIdByReferenceId,
    referenceFilterContext,
    null
  );
  return {
    viewModel: viewModel,
    isViewEmpty: viewModel.nodes.length === 0,
    isUserDefinedGrouping: groupingRules.length > 0,
    viewInstanceId: viewModelState.viewInstanceId,
    performanceTrackingId: viewModelState.performanceTrackingId,
    performanceTrackingData: getPerformanceTrackingData(
      viewModel.rawNodes,
      scopeData
    ),
    viewSettings: viewModelState.viewModelArgs.viewSettings,
    quickPerspectiveReferenceTypeFilters:
      filterInterface.getActiveQuickReferenceTypeFilters(),
    activeConditionalFormattingForLegend:
      getActiveConditionalFormattingForLegend(
        viewModelState.viewModelArgs.i18nLocale
      ),
    isNewConditionalFormatting: false,
    isPresentationMode: viewModelState.viewModelArgs.isPresentationMode,
  };
};

const isConvertedFromWorkspaceMode = ({
  hierarchyDefinition,
}: LoadedGraph): boolean =>
  hierarchyDefinition.some(
    ({ isConvertedFromWorkspaceMode }) => isConvertedFromWorkspaceMode
  );

const withComponentHierarchy = (
  viewModelArgs: BuildViewModelArgs
): BuildViewModelArgs => ({
  ...viewModelArgs,
  graphModel: {
    ...viewModelArgs.graphModel,
    childrenByParent: componentOperations.buildChildrenByParent(
      viewModelArgs.scopeData.components
    ),
    componentsById: viewModelArgs.scopeData.componentsById,
  },
});

/**
 * A slide the presentation build converted from workspace mode, built the way
 * workspace mode built it, from the traversal's data and with viewpoint labels.
 */
const buildConvertedSlideViewModel = (
  { viewModelArgs, bypassLimit }: DependencyMapSourceData,
  {
    scopeData,
    viewInterface,
    filterColorData,
    globalTypeIdByReferenceId,
    referenceFilterContext,
    groupingRules,
    formattingFilters,
    isShowReferenceTypeApplied,
  }: SharedRenderSetup
) =>
  buildWorkspaceModeViewModel(
    withComponentHierarchy(viewModelArgs),
    bypassLimit,
    scopeData,
    filterColorData,
    { ...viewInterface, ...getScopeDataFilterChecks(scopeData) },
    isShowReferenceTypeApplied,
    formattingFilters,
    groupingRules,
    globalTypeIdByReferenceId,
    referenceFilterContext,
    viewInterface
  );

export const toViewpointModeViewModel = (
  viewModelState: DependencyMapSourceData
): DependencyMapViewModelState => {
  const renderSetup = prepareViewpointModeRender(viewModelState);
  const {
    scopeData,
    viewInterface,
    filterColorData,
    globalTypeIdByReferenceId,
    groupingRules,
    groupingData,
    formattingFilters,
    isShowReferenceTypeApplied,
  } = renderSetup;
  const hasReferenceLabels =
    getReferenceLabelFilter(formattingFilters).value !==
    ReferenceLabelSource.NONE;
  const isConverted = isConvertedFromWorkspaceMode(
    viewModelState.viewModelArgs.loadedGraph
  );
  const viewModel = isConverted
    ? buildConvertedSlideViewModel(viewModelState, renderSetup)
    : buildViewpointModeViewModel(
        { ...viewModelState.viewModelArgs, globalTypeIdByReferenceId },
        viewModelState.bypassLimit,
        scopeData,
        filterColorData,
        viewInterface,
        hasReferenceLabels,
        isShowReferenceTypeApplied,
        formattingFilters,
        groupingData
      );
  const { users, context, i18nLocale, isPresentationMode } =
    viewModelState.viewModelArgs;
  const isNewConditionalFormatting = true;
  return {
    viewModel: viewModel,
    isViewEmpty: viewModel.nodes.length === 0,
    isUserDefinedGrouping: groupingRules.length > 0,
    viewInstanceId: viewModelState.viewInstanceId,
    performanceTrackingId: viewModelState.performanceTrackingId,
    performanceTrackingData: getPerformanceTrackingData(
      viewModel.rawNodes,
      scopeData
    ),
    viewSettings: viewModelState.viewModelArgs.viewSettings,
    users,
    selectedComponentId: isConverted ? null : context.componentId || null,
    quickPerspectiveReferenceTypeFilters: null,
    activeConditionalFormattingForLegend: resolveLegendConditionalFormatting(
      scopeData,
      formattingFilters.filter(isFormattingFilter),
      users,
      i18nLocale,
      isNewConditionalFormatting
    ),
    isNewConditionalFormatting,
    isPresentationMode,
  };
};

const updateEmptyStateFromViewModel = pipe(
  startWith<{ isViewEmpty: boolean }>({ isViewEmpty: true }),
  pairwise(),
  filter(([previous, current]) => previous.isViewEmpty || current.isViewEmpty),
  tap(([_previous, { isViewEmpty }]) => {
    dispatchAction(
      updateViewEmptyStateData({
        viewEmptyStateData: { isViewEmpty },
        viewId: VIEW_ID,
      })
    );
  })
);

/**
 * The environment decides what shape its settings controls need and how it is
 * published. Without handlers nothing is derived and nothing is dispatched.
 */
export type DependencyMapSettingsControlsHandlers<TDependencies> = {
  getSettingsControlsData: (
    state: DependencyMapViewStreamedProps
  ) => TDependencies;
  publishSettingsControlsData: (dependencies: TDependencies) => void;
};

const publishSettingsControlsFromViewModel = <TDependencies>({
  getSettingsControlsData,
  publishSettingsControlsData,
}: DependencyMapSettingsControlsHandlers<TDependencies>) =>
  pipe(
    map(getSettingsControlsData),
    distinctUntilChanged<TDependencies>(isEqual),
    tap(publishSettingsControlsData)
  );

const recordPerformanceTrackingFromViewModel = pipe(
  tap(
    ({
      performanceTrackingId,
      performanceTrackingData,
    }: DependencyMapViewStreamedProps) => {
      if (performanceTrackingId && performanceTrackingData) {
        recordViewModelReady(performanceTrackingId, performanceTrackingData);
      }
    }
  )
);

const publishViewComponentIdsFromViewModel = pipe(
  map(({ viewModel }: DependencyMapViewStreamedProps) => viewModel.rawNodes),
  distinctUntilChanged(),
  tap(rawNodes => {
    dispatchAction(
      readViewComponentIds({
        [VIEW_ID]: getDependencyMapViewComponentIds(rawNodes),
      })
    );
  })
);

export const viewModelRoutines = <TDependencies>(
  viewModel$: Observable<DependencyMapViewStreamedProps>,
  settingsControlsHandlers?: DependencyMapSettingsControlsHandlers<TDependencies>
) =>
  collectRoutines(
    viewModel$.pipe(updateEmptyStateFromViewModel),
    viewModel$.pipe(recordPerformanceTrackingFromViewModel),
    ...(settingsControlsHandlers
      ? [
          viewModel$.pipe(
            publishSettingsControlsFromViewModel(settingsControlsHandlers)
          ),
        ]
      : []),
    viewModel$.pipe(publishViewComponentIdsFromViewModel)
  );
